import React, { FC, MutableRefObject, ReactElement, useContext, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'

import Icon, { IconName } from '../../../../React/Common/Components/Icon'
import Loader from '../../../../React/Common/Components/Loader'
import { OAuthConfig } from '../../../../React/Common/Components/OAuthButton/Types/Types'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'
import { executeOAuthFlow } from '../../../../React/Common/Components/OAuthButton/Functions/OAuthFlow'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { configureFullURLForLocalDevelopment } from '../../../../React/Common/Utils/LocalUtils'
import {
    OAUTH_RESPONSE_TYPE,
    OAUTH_WINDOW_SIZING
} from '../../../../React/Common/Components/OAuthButton/Constants/Constants'
import { getCRMOAuthStatus } from '../../../../NonReact/constants/ApiConstants'
import { JSONResponse } from '../../../../NonReact/common/Types/Types'

import { OAuthButton as OAuthButtonLanguage } from '../../../../../i18n'

import { IsStringEmpty, isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { URLConstructor } from '../../../../NonReact/utils/URLBuilder'
import { OAuthStatusResponse } from '../../../../NonReact/Types/Types'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import Base64 from '../../../../NonReact/security/Base64'
import { changeCRMConfiguration } from '../../../../NonReact/common/API/EntityAPIClient'
import { useFormContext, UseFormMethods } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import {
    GROUP_DEVICE_SCOPE_NAME,
    PARTITION_SCOPE_NAME,
    TELEPHONY_SERVER_SCOPE_NAME
} from '../../Organisms/CRMConfiguration/Modal/Constants/FieldNames'

type Props = {
    config?: Nullable<OAuthConfig>
    crmConfigId: string
    isAlreadyAuthenticated?: boolean
    lastError?: Nullable<string>
}

export const CrmOAuthButton: FC<Props> = ({ config, crmConfigId, isAlreadyAuthenticated, lastError }) => {
    const { watch }: UseFormMethods = useFormContext()

    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(OAuthButtonLanguage.NAME)

    const [isAuthorised, setIsAuthorised]: ReactState<boolean> = useState<boolean>(false)
    const [isAuthorising, setIsAuthorising]: ReactState<boolean> = useState<boolean>(false)
    const [authorisationError, setAuthorisationError]: ReactState<Nullable<string>> = useState<Nullable<string>>()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const telephonyServerValue: string = watch(TELEPHONY_SERVER_SCOPE_NAME)
    const partitionValue: string = watch(PARTITION_SCOPE_NAME)
    const deviceValue: string = watch(GROUP_DEVICE_SCOPE_NAME)

    const storeOAuthSession = (sessionId: number): Promise<Nullable<void>> =>
        changeCRMConfiguration(Number(telephonyServerValue), Number(partitionValue), deviceValue, crmConfigId, {
            OAuthSessionID: sessionId.toString()
        })

    const onAuthoriseClick = (): void => {
        if (IsVarEmpty(config)) {
            return
        }

        setIsAuthorised(false)
        setIsAuthorising(true)
        setAuthorisationError(undefined)

        const {
            AuthenticationEndpoint,
            ClientID,
            RedirectURL,
            Scope,
            ExternalUserID,
            AdditionalAuthorisationParameters,
            CodeChallenge,
            CodeChallengeMethod
        }: OAuthConfig = config
        const sessionId: number = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        const sessionIdPart: string = `${ExternalUserID}.${sessionId}.${crmConfigId}`
        const state: string = Base64.urlEncode(`${window.location.origin}|${sessionIdPart}`)

        executeOAuthFlow<OAuthStatusResponse>(
            URLConstructor.newInstance({
                baseURL: AuthenticationEndpoint,
                queryParams: {
                    client_id: ClientID,
                    redirect_uri: configureFullURLForLocalDevelopment(RedirectURL),
                    response_type: OAUTH_RESPONSE_TYPE,
                    state,
                    scope: Scope,
                    ...AdditionalAuthorisationParameters,
                    // PKCE (RFC 7636): included only when the backend supplied a challenge (e.g. Salesforce).
                    ...(isStringNotEmpty(CodeChallenge)
                        ? { code_challenge: CodeChallenge, code_challenge_method: CodeChallengeMethod }
                        : {})
                }
            }),
            `OAuthAuthoriseWindow_${sessionId}`,
            OAUTH_WINDOW_SIZING,
            getCRMOAuthStatus(sessionId),
            (status: JSONResponse<OAuthStatusResponse>): boolean => !!status?.HasRefreshToken,
            language.get(OAuthButtonLanguage.Authentication_Failed)
        )
            .then(() => storeOAuthSession(sessionId))
            .then((): void => {
                if (isMounted.current) {
                    setIsAuthorising(false)
                    setIsAuthorised(true)
                }
            })
            .catch((err: Error): void => {
                if (isMounted.current) {
                    setAuthorisationError(err?.message)
                    setIsAuthorising(false)
                    setIsAuthorised(false)
                }
            })
    }

    const createStatusElement = (icon: IconName, text: string, warning?: Nullable<string>): ReactElement => (
        <>
            <div
                className={`ocr-status-container ${
                    icon === IconName.SUCCESS ? 'ocr-status-success' : 'ocr-status-error'
                }`}
            >
                <Icon className={`${icon === IconName.SUCCESS ? 'success' : 'error'}-oauth-icon`} iconName={icon} />
                <span className='status-text'>{text}</span>
            </div>
            {isStringNotEmpty(warning) ? (
                <div className='ocr-warning'>
                    <Icon className='warning-oauth-icon' iconName={IconName.WARNING} />
                    <span className='status-text'>{lastError}</span>
                </div>
            ) : (
                <></>
            )}
        </>
    )

    if (isStringNotEmpty(config?.AuthenticationEndpoint)) {
        return (
            <div className='oauth-container'>
                <div className='oc-button'>
                    <RoundedButton type='button' onClick={onAuthoriseClick} shrink>
                        {language.get(OAuthButtonLanguage.AuthoriseButton_Title)}
                    </RoundedButton>
                </div>
                {isAuthorised || isAuthorising || isAlreadyAuthenticated || isStringNotEmpty(authorisationError) ? (
                    <div className='oc-status'>
                        <div className='oc-status-title'>
                            {language.get(OAuthButtonLanguage.Authentication_Status_Title)}
                        </div>
                        <div className='oc-info-container'>
                            {isVarNotEmpty(authorisationError) ? (
                                createStatusElement(IconName.ERRORCIRCLE, authorisationError)
                            ) : (
                                <></>
                            )}
                            {isAlreadyAuthenticated &&
                            !isAuthorising &&
                            !isAuthorised &&
                            IsStringEmpty(authorisationError) ? (
                                createStatusElement(
                                    IconName.SUCCESS,
                                    language.get(OAuthButtonLanguage.Authentication_CrmAuthenticated),
                                    lastError
                                )
                            ) : (
                                <></>
                            )}
                            {isAuthorised ? (
                                createStatusElement(
                                    IconName.SUCCESS,
                                    language.get(OAuthButtonLanguage.Authentication_Successful)
                                )
                            ) : (
                                <></>
                            )}
                            {isAuthorising ? <Loader /> : <></>}
                        </div>
                    </div>
                ) : (
                    <></>
                )}
            </div>
        )
    }

    return <></>
}
